import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  MessageSquare, X, Send, Plus, Trash2, Sparkles, Loader2, ChevronLeft,
  ExternalLink, Zap,
} from "lucide-react";
import api, { formatApiError, API_BASE } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";

// Prose overrides for react-markdown so assistant answers render with proper
// bullets / bold / inline code inside the compact chat bubble. Hoisted to
// module scope so React re-renders don't destroy the subtree.
const MD_COMPONENTS = {
  p: ({ node, ...p }) => <p className="mb-2 last:mb-0" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-5 space-y-1 mb-2 last:mb-0" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-5 space-y-1 mb-2 last:mb-0" {...p} />,
  li: ({ node, ...p }) => <li className="leading-snug" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-heading text-ink" {...p} />,
  em: ({ node, ...p }) => <em className="italic" {...p} />,
  code: ({ node, inline, ...p }) => inline
    ? <code className="font-mono text-[0.85em] bg-navy/[0.06] rounded px-1 py-0.5 text-[#8A6600]" {...p} />
    : <code className="block font-mono text-[0.85em] bg-navy/[0.06] rounded p-2 my-2 whitespace-pre-wrap" {...p} />,
  a: ({ node, ...p }) => <a className="text-gold underline hover:text-[#8A6600]" target="_blank" rel="noreferrer" {...p} />,
  h1: ({ node, ...p }) => <h3 className="font-heading text-base mt-1 mb-1.5" {...p} />,
  h2: ({ node, ...p }) => <h3 className="font-heading text-base mt-1 mb-1.5" {...p} />,
  h3: ({ node, ...p }) => <h3 className="font-heading text-sm mt-1 mb-1" {...p} />,
  hr: () => <hr className="my-2 border-navy/10" />,
};

/**
 * Ask VenturaZ — floating chat widget (bottom-right).
 *
 * Two panes: sidebar of past sessions (ChatGPT-style) + current thread.
 * Personas (product vs admin) are driven server-side from the caller's
 * role so the LLM answers know whether you're in the ops cockpit.
 *
 * Auth-gated: nothing renders while logged-out. Persistent state lives in
 * MongoDB via /api/ai-chat/*.
 */
export default function AskVenturazWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null); // {session, messages}
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [copilotState, setCopilotState] = useState(null); // {startup_name, kyc_status, gaps}
  const [copilotBusy, setCopilotBusy] = useState(null); // gap_id being guided
  const [copilotAnswer, setCopilotAnswer] = useState(null); // {gap, answer}
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const isFounder = (user?.role || "").toLowerCase() === "startup";
  const isInvestor = (user?.role || "").toLowerCase() === "investor";
  const isAdmin = ["super_admin", "admin", "manager", "agent"].includes((user?.role || "").toLowerCase());
  const copilotEnabled = isFounder || isInvestor;
  const copilotKind = isInvestor ? "investor" : "founder";

  const loadCopilot = useCallback(async (silent = false) => {
    if (!copilotEnabled) return;
    try {
      const url = isInvestor ? "/ai-chat/investor-copilot/gaps" : "/ai-chat/kyc-copilot/gaps";
      const { data } = await api.get(url);
      // Celebratory toast — when the gap count drops between polls, tell the
      // user which Trust Score / DD-unlock delta they just earned.
      setCopilotState((prev) => {
        if (!silent && prev?.gaps && data?.gaps && prev.gaps.length > data.gaps.length) {
          const resolvedIds = new Set(data.gaps.map((g) => g.id));
          const winners = prev.gaps.filter((g) => !resolvedIds.has(g.id));
          const delta = winners.reduce((n, g) => n + (Number(g.impact) || 0), 0);
          const label = winners.map((g) => g.label.split(/[.—:]/)[0]).join(", ");
          toast.success(
            isInvestor
              ? `Nice — ${label} resolved. That unlocks new investor access.`
              : `Trust Score +${delta || "?"} pts — ${label} resolved!`,
            { duration: 6000 },
          );
        }
        return data;
      });
    } catch (e) {
      // 404 = no profile yet — that's fine, hide the copilot.
      if (e?.response?.status !== 404) toast.error(formatApiError(e));
      setCopilotState(null);
    }
  }, [copilotEnabled, isInvestor]);

  const askCopilot = async (gapId) => {
    setCopilotBusy(gapId);
    setCopilotAnswer(null);
    try {
      const url = isInvestor ? "/ai-chat/investor-copilot/guide" : "/ai-chat/kyc-copilot/guide";
      const { data } = await api.post(url, { gap_id: gapId });
      setCopilotAnswer(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCopilotBusy(null); }
  };

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/ai-chat/sessions");
      setSessions(data?.sessions || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => {
    if (open && user) loadSessions();
  }, [open, user, loadSessions]);

  useEffect(() => {
    if (copilotOpen && copilotEnabled) loadCopilot(true);
  }, [copilotOpen, copilotEnabled, loadCopilot]);

  // Silent re-poll when the widget regains focus — catches a founder who
  // completed a verification in a new tab and comes back to the chat.
  useEffect(() => {
    if (!copilotOpen || !copilotEnabled) return;
    const onFocus = () => loadCopilot(false);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [copilotOpen, copilotEnabled, loadCopilot]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [active?.messages?.length, sending]);

  const openSession = async (sessionId) => {
    setLoadingSession(true);
    setShowSidebar(false);
    try {
      const { data } = await api.get(`/ai-chat/sessions/${sessionId}`);
      setActive({ session: data.session, messages: data.messages || [] });
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingSession(false); }
  };

  const newSession = async () => {
    try {
      const { data } = await api.post("/ai-chat/sessions", {});
      const s = data?.session;
      if (!s) return;
      setSessions((prev) => [s, ...prev]);
      setActive({ session: s, messages: [] });
      setShowSidebar(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteSession = async (sessionId, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this chat? Messages are gone for good.")) return;
    try {
      await api.delete(`/ai-chat/sessions/${sessionId}`);
      setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
      if (active?.session?.session_id === sessionId) setActive(null);
      toast.success("Chat deleted.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    let session = active?.session;
    // Auto-create a session on first send if the user just opened the widget.
    if (!session) {
      try {
        const { data } = await api.post("/ai-chat/sessions", {});
        session = data.session;
        setSessions((prev) => [session, ...prev]);
      } catch (e) { toast.error(formatApiError(e)); return; }
    }
    setSending(true);
    setDraft("");
    // Optimistic user turn
    const optimistic = {
      message_id: `tmp-${Date.now()}`, role: "user", content: text,
      created_at: new Date().toISOString(),
    };
    setActive({ session, messages: [...(active?.messages || []), optimistic] });

    // Streaming path first (SSE). Falls back to JSON POST when the browser
    // can't reach the stream endpoint (e.g. ingress buffered the response).
    const token = window.localStorage.getItem("token") || "";
    const streamUrl = `${API_BASE}/ai-chat/sessions/${session.session_id}/messages/stream`;
    let usedStream = false;
    try {
      const res = await fetch(streamUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ text }),
      });
      if (!res.ok || !res.body) throw new Error(`stream_http_${res.status}`);

      // Placeholder assistant message that will accumulate stream deltas.
      const placeholder = {
        message_id: `stream-${Date.now()}`,
        role: "assistant",
        content: "",
        created_at: new Date().toISOString(),
        streaming: true,
      };
      setActive((prev) => ({
        session: prev.session,
        messages: [...prev.messages.filter((m) => m.message_id !== optimistic.message_id), optimistic, placeholder],
      }));

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let finalized = false;
      while (!finalized) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // Naive SSE parser: split on \n\n frames.
        const frames = buf.split("\n\n");
        buf = frames.pop() || "";
        for (const frame of frames) {
          const evMatch = frame.match(/^event:\s*(\S+)/m);
          const dataMatch = frame.match(/^data:\s*(.+)$/m);
          if (!evMatch || !dataMatch) continue;
          const evName = evMatch[1];
          let payload;
          try { payload = JSON.parse(dataMatch[1]); } catch { continue; }
          if (evName === "delta") {
            setActive((prev) => {
              const msgs = [...prev.messages];
              const last = msgs[msgs.length - 1];
              if (last && last.streaming) msgs[msgs.length - 1] = { ...last, content: (last.content || "") + (payload.content || "") };
              return { ...prev, messages: msgs };
            });
          } else if (evName === "done") {
            usedStream = true;
            finalized = true;
            setActive((prev) => {
              const msgs = [...prev.messages];
              const idx = msgs.findIndex((m) => m.streaming);
              if (idx >= 0) msgs[idx] = payload.assistant_message;
              return {
                session: { ...prev.session, ...(payload.session_updates || {}) },
                messages: msgs,
              };
            });
            setSessions((prev) => prev.map((s) => s.session_id === session.session_id ? { ...s, ...(payload.session_updates || {}) } : s));
          } else if (evName === "error") {
            throw new Error(payload.error || "stream error");
          }
        }
      }
    } catch (streamErr) {
      // Fallback to non-streaming JSON.
      if (!usedStream) {
        try {
          const { data } = await api.post(`/ai-chat/sessions/${session.session_id}/messages`, { text });
          setActive((prev) => {
            const filtered = (prev?.messages || []).filter((m) => m.message_id !== optimistic.message_id && !m.streaming);
            return {
              session: { ...prev.session, ...(data.session_updates || {}) },
              messages: [...filtered, data.user_message, data.assistant_message],
            };
          });
          setSessions((prev) => {
            const patch = data.session_updates || {};
            return prev.map((s) => s.session_id === session.session_id ? { ...s, ...patch } : s);
          });
        } catch (e) {
          toast.error(formatApiError(e));
          setActive((prev) => ({
            ...prev,
            messages: (prev?.messages || []).filter((m) => m.message_id !== optimistic.message_id && !m.streaming),
          }));
        }
      }
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  };

  if (!user) return null; // Hide until logged-in

  // Collapsed launcher — warm, playful, mascot-forward
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-[110] group flex items-center gap-2 pl-2 pr-4 py-2 rounded-full bg-gradient-to-br from-navy-deep via-navy to-navy-midnight text-white shadow-[0_10px_30px_rgba(15,61,46,0.35)] hover:shadow-[0_16px_40px_rgba(15,61,46,0.45)] hover:-translate-y-0.5 transition-all animate-pulse-warm"
        data-testid="ask-venturaz-launcher"
        aria-label="Open Ask VenturaZ chat"
      >
        <span className="relative flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-sunset to-butter shadow-inner">
          {/* Little friendly companion — soft rounded shape with sparkle */}
          <svg viewBox="0 0 32 32" className="w-6 h-6" aria-hidden>
            <ellipse cx="16" cy="18" rx="10" ry="9" fill="#0F3D2E" />
            <circle cx="12" cy="17" r="1.6" fill="#FFD700" />
            <circle cx="20" cy="17" r="1.6" fill="#FFD700" />
            <path d="M12 22 Q16 25 20 22" stroke="#FFD700" strokeWidth="1.6" fill="none" strokeLinecap="round" />
          </svg>
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-mint border-2 border-white animate-pulse" />
        </span>
        <span className="text-xs font-heading tracking-wide">
          Ask <span className="text-gold">VenturaZ</span>
        </span>
      </button>
    );
  }

  const messages = active?.messages || [];

  return (
    <div
      className="fixed bottom-5 right-5 z-[110] w-[95vw] max-w-md h-[75vh] max-h-[720px] bg-white rounded-[28px] shadow-[0_20px_60px_rgba(15,61,46,0.25)] border border-parchment flex overflow-hidden animate-fade-slide-up"
      data-testid="ask-venturaz-panel"
    >
      {/* Sidebar */}
      {showSidebar && (
        <aside className="w-40 flex-shrink-0 border-r border-navy/10 bg-navy/[0.02] flex flex-col" data-testid="ask-venturaz-sidebar">
          <div className="p-2 border-b border-navy/10 flex items-center gap-1.5">
            <button
              onClick={newSession}
              className="flex-1 inline-flex items-center gap-1 px-2 py-1.5 rounded bg-gold text-navy text-[11px] font-heading hover:bg-gold/90"
              data-testid="ask-venturaz-new"
            >
              <Plus size={11} /> New
            </button>
            <button
              onClick={() => setShowSidebar(false)}
              className="p-1.5 rounded hover:bg-navy/[0.05] text-ink-muted"
              aria-label="Close sidebar"
              data-testid="ask-venturaz-sidebar-close"
            >
              <ChevronLeft size={13} />
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {sessions.length === 0 && (
              <div className="p-3 text-[11px] text-ink-muted italic">No past chats.</div>
            )}
            {sessions.map((s) => {
              const isActive = active?.session?.session_id === s.session_id;
              return (
                <button
                  key={s.session_id}
                  onClick={() => openSession(s.session_id)}
                  className={`w-full text-left px-2 py-2 border-b border-navy/[0.04] hover:bg-navy/[0.05] group ${isActive ? "bg-gold/[0.10]" : ""}`}
                  data-testid={`ask-venturaz-session-${s.session_id}`}
                >
                  <div className="flex items-center justify-between gap-1">
                    <div className="text-[11px] font-heading text-ink truncate min-w-0 flex-1">{s.title || "New chat"}</div>
                    <button
                      onClick={(e) => deleteSession(s.session_id, e)}
                      className="opacity-0 group-hover:opacity-100 text-rose-600 hover:text-rose-800 shrink-0"
                      aria-label="Delete chat"
                      data-testid={`ask-venturaz-session-delete-${s.session_id}`}
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                  <div className="text-[9px] font-mono text-ink-muted">{s.message_count || 0} msgs</div>
                </button>
              );
            })}
          </div>
        </aside>
      )}

      {/* Main chat pane */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <header className="flex items-center justify-between px-4 py-3 border-b border-parchment bg-gradient-to-br from-navy-deep via-navy to-navy-midnight text-white relative overflow-hidden">
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-sunset/25 blur-2xl pointer-events-none" aria-hidden />
          <div className="absolute -bottom-10 -left-6 w-24 h-24 rounded-full bg-mint/20 blur-2xl pointer-events-none" aria-hidden />
          <div className="relative flex items-center gap-2.5 min-w-0">
            {!showSidebar && (
              <button
                onClick={() => setShowSidebar(true)}
                className="p-1.5 rounded-full hover:bg-white/10"
                aria-label="Open chat history"
                data-testid="ask-venturaz-sidebar-open"
              >
                <MessageSquare size={14} />
              </button>
            )}
            {/* Mascot avatar */}
            <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-sunset to-butter flex items-center justify-center shadow-inner shrink-0">
              <svg viewBox="0 0 32 32" className="w-6 h-6" aria-hidden>
                <ellipse cx="16" cy="18" rx="10" ry="9" fill="#0F3D2E" />
                <circle cx="12" cy="17" r="1.6" fill="#FFD700" />
                <circle cx="20" cy="17" r="1.6" fill="#FFD700" />
                <path d="M12 22 Q16 25 20 22" stroke="#FFD700" strokeWidth="1.6" fill="none" strokeLinecap="round" />
              </svg>
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-mint border-2 border-navy-deep animate-pulse" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-white/70">
                Ask <span className="text-gold">VenturaZ</span>
              </div>
              <div className="text-sm font-heading truncate text-white/95" data-testid="ask-venturaz-title">
                {copilotOpen ? "KYC Copilot" : (active?.session?.title || "How can I help today?")}
              </div>
            </div>
          </div>
          <div className="relative flex items-center gap-1">
            {copilotEnabled && (
              <button
                onClick={() => setCopilotOpen((v) => !v)}
                className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1 transition-colors ${copilotOpen ? "bg-gold text-navy-deep" : "bg-white/10 hover:bg-white/20 text-white"}`}
                data-testid="ask-venturaz-copilot-toggle"
                title={isInvestor
                  ? "Guided investor readiness — unblock Deep DD access"
                  : "Guided KYC coaching prioritised by Trust Score impact"}
              >
                <Zap size={11} /> Copilot
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-full hover:bg-white/10"
              aria-label="Close chat"
              data-testid="ask-venturaz-close"
            >
              <X size={14} />
            </button>
          </div>
        </header>

        {/* Message list */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-parchment/60 via-white to-white" data-testid="ask-venturaz-messages">
          {copilotOpen && (
            <div className="space-y-2" data-testid="ask-venturaz-copilot-pane">
              {!copilotState && (
                <div className="text-center py-4 text-xs text-ink-muted">
                  <Loader2 size={14} className="animate-spin inline mr-1" /> Reading your KYC profile…
                </div>
              )}
              {copilotState && copilotState.gaps.length === 0 && (
                <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-center" data-testid="ask-venturaz-copilot-clean">
                  <Sparkles size={20} className="text-emerald-600 mx-auto mb-1.5" />
                  <div className="font-heading text-emerald-900 text-sm">You are fully verified.</div>
                  <div className="text-[11px] text-emerald-800/80 mt-1">
                    {isInvestor
                      ? "No open readiness gaps — Deep DD orders, deal-room access and the investor badge are all unlocked."
                      : "No open KYC gaps — nothing to fix. Your Trust Score is maxed on this axis."}
                  </div>
                </div>
              )}
              {copilotState && copilotState.gaps.length > 0 && (
                <>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">
                    {(copilotState.startup_name || copilotState.investor_name) || (isInvestor ? "Your investor profile" : "Your startup")} · {copilotState.gaps.length} gap{copilotState.gaps.length === 1 ? "" : "s"} · sorted by {isInvestor ? "severity" : "impact"}
                  </div>
                  {copilotState.gaps.map((g) => (
                    <div
                      key={g.id}
                      className="rounded-lg border border-navy/10 bg-white p-3"
                      data-testid={`ask-venturaz-copilot-gap-${g.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="font-heading text-sm text-ink leading-snug">{g.label}</div>
                        <span className={`shrink-0 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                          isInvestor
                            ? (g.severity === "blocker" ? "bg-rose-100 text-rose-800 border-rose-300" : "bg-amber-100 text-amber-800 border-amber-300")
                            : "bg-gold/20 text-[#8A6600] border-gold/40"
                        }`}>
                          {isInvestor ? g.severity : `+${g.impact} pts`}
                        </span>
                      </div>
                      <div className="text-[11px] text-ink-muted mb-1">{g.how_to}</div>
                      {isInvestor && g.unlocks && (
                        <div className="text-[11px] text-emerald-700 mb-2">
                          Unlocks: {g.unlocks}
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 mt-1">
                        <Link
                          to={g.cta_href}
                          onClick={() => setOpen(false)}
                          className="text-[11px] px-2 py-1 rounded bg-navy text-cream font-heading hover:bg-[#256E58] inline-flex items-center gap-1"
                          data-testid={`ask-venturaz-copilot-open-${g.id}`}
                        >
                          <ExternalLink size={10} /> {g.cta_label}
                        </Link>
                        <button
                          onClick={() => askCopilot(g.id)}
                          disabled={copilotBusy === g.id}
                          className="text-[11px] px-2 py-1 rounded border border-navy/15 text-ink hover:border-gold hover:text-[#8A6600] inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`ask-venturaz-copilot-ask-${g.id}`}
                        >
                          {copilotBusy === g.id ? <><Loader2 size={10} className="animate-spin" /> Asking…</> : <><Sparkles size={10} /> Ask AI</>}
                        </button>
                      </div>
                      {copilotAnswer?.gap?.id === g.id && (
                        <div className="mt-2 pt-2 border-t border-dashed border-navy/15 text-xs text-ink" data-testid={`ask-venturaz-copilot-answer-${g.id}`}>
                          <ReactMarkdown components={MD_COMPONENTS}>
                            {copilotAnswer.answer || ""}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
          {!copilotOpen && loadingSession && (
            <div className="flex items-center justify-center gap-2 text-ink-muted text-xs py-6">
              <Loader2 size={13} className="animate-spin" /> Loading chat…
            </div>
          )}
          {!copilotOpen && !loadingSession && messages.length === 0 && (
            <div className="text-center py-6 space-y-3" data-testid="ask-venturaz-empty">
              <div className="mx-auto w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center">
                <Sparkles size={16} className="text-gold" />
              </div>
              <div className="font-heading text-ink text-sm">Ask me anything about VenturaZ.</div>
              <div className="text-[11px] text-ink-muted max-w-[280px] mx-auto">
                I know the Trust Score, Deep DD, KYC Queue, deal rooms, e-sign flow — anything you can see in your dashboard.
              </div>
              <div className="flex flex-wrap justify-center gap-1.5 pt-1">
                {(isAdmin
                  ? ["/queue", "/breach", "/lookup GreenGrid"]
                  : ["How does the Trust Score work?", "What is Basic Health Check?", "How do I complete KYC?"]
                ).map((suggest) => (
                  <button
                    key={suggest}
                    onClick={() => setDraft(suggest)}
                    className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border border-navy/15 text-ink-soft bg-white hover:border-gold hover:text-[#8A6600]"
                    data-testid={`ask-venturaz-suggest-${suggest.slice(0,10)}`}
                  >
                    {suggest}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!copilotOpen && messages.map((m) => {
            const mine = m.role === "user";
            const chips = (!mine && Array.isArray(m.deep_links)) ? m.deep_links : [];
            return (
              <div
                key={m.message_id}
                className={`flex flex-col ${mine ? "items-end" : "items-start"}`}
                data-testid={`ask-venturaz-msg-${m.role}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                    mine
                      ? "bg-navy text-cream rounded-br-none whitespace-pre-wrap"
                      : "bg-white text-ink border border-navy/10 rounded-bl-none"
                  }`}
                >
                  {mine ? (
                    m.content
                  ) : (
                    <div className="text-sm leading-relaxed" data-testid={`ask-venturaz-md-${m.message_id}`}>
                      <ReactMarkdown components={MD_COMPONENTS}>
                        {m.content || ""}
                      </ReactMarkdown>
                    </div>
                  )}
                  {m.streaming && (
                    <span className="inline-block w-1 h-3 ml-0.5 bg-ink/60 animate-pulse align-middle" aria-hidden />
                  )}
                </div>
                {chips.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5 max-w-[85%]" data-testid={`ask-venturaz-links-${m.message_id}`}>
                    {chips.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setOpen(false)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gold/15 border border-gold/40 text-[10px] font-mono uppercase tracking-widest text-[#8A6600] hover:bg-gold/30"
                        data-testid={link.testid || `ask-venturaz-deep-link`}
                      >
                        <ExternalLink size={9} /> {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {!copilotOpen && sending && (
            <div className="flex justify-start" data-testid="ask-venturaz-typing">
              <div className="px-3 py-2 rounded-lg text-sm bg-white text-ink-muted border border-navy/10 rounded-bl-none flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-navy/60 animate-bounce" />
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-navy/60 animate-bounce" style={{ animationDelay: "0.15s" }} />
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-navy/60 animate-bounce" style={{ animationDelay: "0.3s" }} />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Composer */}
        {!copilotOpen && (
        <footer className="p-2 border-t border-navy/10 bg-white">
          <div className="flex items-end gap-1.5">
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder="Ask about anything on the platform…"
              rows={1}
              className="flex-1 text-sm bg-white border border-navy/15 rounded-md p-2 text-ink placeholder:text-ink-faint focus:border-gold outline-none resize-none max-h-32"
              data-testid="ask-venturaz-input"
              disabled={sending}
            />
            <button
              onClick={send}
              disabled={!draft.trim() || sending}
              className="p-2 rounded-md bg-gold text-navy hover:bg-gold/90 disabled:opacity-40 shrink-0"
              data-testid="ask-venturaz-send"
              aria-label="Send"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-ink-muted mt-1 px-1">
            Enter to send · Shift+Enter for a new line
          </div>
        </footer>
        )}
      </div>
    </div>
  );
}
